import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material'
import {
  Assessment as AssessmentIcon,
  Assignment as AssignmentIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Folder as FolderIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import apiClient from '../services/api'

interface KPIData {
  studies: {
    total: number
    completed: number
    inProgress: number
    reassigned: number
    overdue: number
    urgent: number
    normal: number
  }
  patients: {
    total: number
    active: number
    newThisMonth: number
  }
  documents: {
    total: number
    dicom: number
    nonDicom: number
  }
  reports: {
    total: number
    completed: number
    draft: number
    pending: number
  }
  performance: {
    avgCompletionTime: number
    slaComplianceRate: number
    avgPickupTime: number
    throughput: number
  }
  users: {
    total: number
    active: number
    medics: number
    operators: number
  }
  shifts: {
    total: number
    active: number
    completed: number
  }
}

const KPIPage: React.FC = () => {
  const { user } = useAuth()
  const [kpiData, setKpiData] = useState<KPIData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchKPIData()
  }, [])

  const fetchKPIData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch data from multiple endpoints
      let studiesUrl = '/worklist?limit=200';
      let patientsUrl = '/patients?limit=200';
      let documentsUrl = '/documents?limit=200';
      let reportsUrl = '/reports?limit=200';
      let shiftsUrl = '/shifts?limit=200';
      
      // Pentru medic, filtrează doar datele lui
      if (user?.role === 'medic') {
        studiesUrl = `/?assigned_to=${user.id}&limit=200`;
        patientsUrl = `/patients?assigned_to_medic=${user.id}&limit=200`;
        documentsUrl = `/documents?assigned_to_medic=${user.id}&limit=200`;
        reportsUrl = `/reports?author_id=${user.id}&limit=200`;
        shiftsUrl = `/shifts?doctor_id=${user.id}&limit=200`;
      }
      
      const [
        studiesResponse,
        patientsResponse,
        documentsResponse,
        reportsResponse,
        usersResponse,
        shiftsResponse,
      ] = await Promise.all([
        apiClient.get(studiesUrl),
        apiClient.get(patientsUrl),
        apiClient.get(documentsUrl),
        apiClient.get(reportsUrl),
        apiClient.get('/users?limit=200'), // Users (maxim permis)
        apiClient.get(shiftsUrl),
      ])

      // Process studies data
      const studies = studiesResponse.data.studies || []
      const studiesData = {
        total: studies.length,
        completed: studies.filter((s: any) => s.status === 'completed').length,
        inProgress: studies.filter((s: any) => s.status === 'in_progress').length,
        reassigned: studies.filter((s: any) => s.status === 'reassigned').length,
        overdue: studies.filter((s: any) => s.status === 'overdue').length,
        urgent: studies.filter((s: any) => s.priority === 'urgent').length,
        normal: studies.filter((s: any) => s.priority === 'normal').length,
      }

      // Process patients data
      const patients = patientsResponse.data.patients || []
      const patientsData = {
        total: patients.length,
        active: patients.filter((p: any) => !p.deleted_at).length,
        newThisMonth: patients.filter((p: any) => {
          const created = new Date(p.created_at)
          const now = new Date()
          return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear()
        }).length,
      }

      // Process documents data
      const documents = documentsResponse.data.documents || []
      const documentsData = {
        total: documents.length,
        dicom: documents.filter((d: any) => d.type === 'dicom').length,
        nonDicom: documents.filter((d: any) => d.type !== 'dicom').length,
      }

      // Process reports data
      const reports = reportsResponse.data.reports || []
      const reportsData = {
        total: reports.length,
        completed: reports.filter((r: any) => r.status === 'completed').length,
        draft: reports.filter((r: any) => r.status === 'draft').length,
        pending: reports.filter((r: any) => r.status === 'pending').length,
      }

      // Process users data
      const users = usersResponse.data.users || []
      const usersData = {
        total: users.length,
        active: users.filter((u: any) => !u.deleted_at).length,
        medics: users.filter((u: any) => u.role === 'medic' && !u.deleted_at).length,
        operators: users.filter((u: any) => u.role === 'operator' && !u.deleted_at).length,
      }

      // Process shifts data
      const shifts = shiftsResponse.data.shifts || []
      const shiftsData = {
        total: shifts.length,
        active: shifts.filter((s: any) => s.active).length,
        completed: shifts.filter((s: any) => !s.active).length,
      }

      // Calculate performance metrics
      const completedStudies = studies.filter((s: any) => s.status === 'completed')
      const avgCompletionTime = completedStudies.length > 0 
        ? completedStudies.reduce((acc: number, s: any) => {
            if (s.assigned_at && s.completed_at) {
              const assigned = new Date(s.assigned_at)
              const completed = new Date(s.completed_at)
              return acc + (completed.getTime() - assigned.getTime()) / (1000 * 60 * 60) // hours
            }
            return acc
          }, 0) / completedStudies.length
        : 0

      const slaComplianceRate = completedStudies.length > 0 
        ? completedStudies.filter((s: any) => {
            if (s.assigned_at && s.completed_at) {
              const assigned = new Date(s.assigned_at)
              const completed = new Date(s.completed_at)
              const hours = (completed.getTime() - assigned.getTime()) / (1000 * 60 * 60)
              return s.priority === 'urgent' ? hours <= 2 : hours <= 24 // 2h urgent, 24h normal
            }
            return false
          }).length / completedStudies.length * 100
        : 0

      const performanceData = {
        avgCompletionTime: Math.round(avgCompletionTime * 10) / 10,
        slaComplianceRate: Math.round(slaComplianceRate),
        avgPickupTime: 0, // Would need scoring data
        throughput: Math.round(completedStudies.length / 30), // per day (assuming 30 days)
      }

      setKpiData({
        studies: studiesData,
        patients: patientsData,
        documents: documentsData,
        reports: reportsData,
        performance: performanceData,
        users: usersData,
        shifts: shiftsData,
      })

    } catch (err: any) {
      console.error('Error fetching KPI data:', err)
      setError(err.response?.data?.detail || 'Eroare la încărcarea datelor KPI')
    } finally {
      setLoading(false)
    }
  }



  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  if (!kpiData) {
    return (
      <Box p={3}>
        <Alert severity="info">Nu sunt date disponibile</Alert>
      </Box>
    )
  }

  return (
    <Box p={1.5}>
      {/* Main KPI Cards - Compact & Fixed Height */}
      <Grid container spacing={1.5} mb={1.5} alignItems="stretch">
        {/* Studii */}
        <Grid item xs={6} lg={3} sx={{ display: 'flex' }}>
          <Card sx={{ bgcolor: 'background.paper', width: '100%', height: 130 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" mb={0.5}>
                <AssignmentIcon color="primary" sx={{ mr: 0.5, fontSize: 18 }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Studii</Typography>
              </Box>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'primary.main', lineHeight: 1.2 }}>{kpiData.studies.total}</Typography>
              <Box display="flex" justifyContent="space-between" mb={0.25}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Finalizate</Typography><Chip label={kpiData.studies.completed} size="small" color="success" sx={{ height: 18, fontSize: '0.68rem' }} /></Box>
              <Box display="flex" justifyContent="space-between" mb={0.25}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>În lucru</Typography><Chip label={kpiData.studies.inProgress} size="small" color="warning" sx={{ height: 18, fontSize: '0.68rem' }} /></Box>
              <Box display="flex" justifyContent="space-between"><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Depășite</Typography><Chip label={kpiData.studies.overdue} size="small" color="error" sx={{ height: 18, fontSize: '0.68rem' }} /></Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Pacienți */}
        <Grid item xs={6} lg={3} sx={{ display: 'flex' }}>
          <Card sx={{ bgcolor: 'background.paper', width: '100%', height: 130 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" mb={0.5}>
                <PeopleIcon color="primary" sx={{ mr: 0.5, fontSize: 18 }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Pacienți</Typography>
              </Box>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'primary.main', lineHeight: 1.2 }}>{kpiData.patients.total}</Typography>
              <Box display="flex" justifyContent="space-between" mb={0.25}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Activi</Typography><Chip label={kpiData.patients.active} size="small" color="success" sx={{ height: 18, fontSize: '0.68rem' }} /></Box>
              <Box display="flex" justifyContent="space-between" mb={0.25}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Noi luna asta</Typography><Chip label={kpiData.patients.newThisMonth} size="small" color="info" sx={{ height: 18, fontSize: '0.68rem' }} /></Box>
              <Box display="flex" justifyContent="space-between"><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>&nbsp;</Typography></Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Rapoarte */}
        <Grid item xs={6} lg={3} sx={{ display: 'flex' }}>
          <Card sx={{ bgcolor: 'background.paper', width: '100%', height: 130 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" mb={0.5}>
                <DescriptionIcon color="primary" sx={{ mr: 0.5, fontSize: 18 }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Rapoarte</Typography>
              </Box>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'primary.main', lineHeight: 1.2 }}>{kpiData.reports.total}</Typography>
              <Box display="flex" justifyContent="space-between" mb={0.25}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Finalizate</Typography><Chip label={kpiData.reports.completed} size="small" color="success" sx={{ height: 18, fontSize: '0.68rem' }} /></Box>
              <Box display="flex" justifyContent="space-between" mb={0.25}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Draft</Typography><Chip label={kpiData.reports.draft} size="small" color="warning" sx={{ height: 18, fontSize: '0.68rem' }} /></Box>
              <Box display="flex" justifyContent="space-between"><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>&nbsp;</Typography></Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Documente */}
        <Grid item xs={6} lg={3} sx={{ display: 'flex' }}>
          <Card sx={{ bgcolor: 'background.paper', width: '100%', height: 130 }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box display="flex" alignItems="center" mb={0.5}>
                <FolderIcon color="primary" sx={{ mr: 0.5, fontSize: 18 }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Documente</Typography>
              </Box>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: 'primary.main', lineHeight: 1.2 }}>{kpiData.documents.total}</Typography>
              <Box display="flex" justifyContent="space-between" mb={0.25}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>DICOM</Typography><Chip label={kpiData.documents.dicom} size="small" color="primary" sx={{ height: 18, fontSize: '0.68rem' }} /></Box>
              <Box display="flex" justifyContent="space-between" mb={0.25}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Non-DICOM</Typography><Chip label={kpiData.documents.nonDicom} size="small" color="secondary" sx={{ height: 18, fontSize: '0.68rem' }} /></Box>
              <Box display="flex" justifyContent="space-between"><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>&nbsp;</Typography></Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Metrics - Compact & Fixed Height */}
      <Grid container spacing={1.5} mb={1.5} alignItems="stretch">
        <Grid item xs={12} lg={8} sx={{ display: 'flex' }}>
          <Paper sx={{ p: 1.5, bgcolor: 'background.paper', width: '100%', height: 70 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 0.5 }}>Metrici Performanță</Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Box display="flex" alignItems="center"><AccessTimeIcon sx={{ mr: 0.5, fontSize: 16, color: 'primary.main' }} /><Typography sx={{ fontSize: '0.78rem' }}>Finalizare: {kpiData.performance.avgCompletionTime}h</Typography></Box>
              <Box display="flex" alignItems="center"><CheckCircleIcon sx={{ mr: 0.5, fontSize: 16, color: 'success.main' }} /><Typography sx={{ fontSize: '0.78rem' }}>SLA: {kpiData.performance.slaComplianceRate}%</Typography></Box>
              <Box display="flex" alignItems="center"><SpeedIcon sx={{ mr: 0.5, fontSize: 16, color: 'info.main' }} /><Typography sx={{ fontSize: '0.78rem' }}>Throughput: {kpiData.performance.throughput}/zi</Typography></Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
          <Paper sx={{ p: 1.5, bgcolor: 'background.paper', width: '100%', height: 70 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 0.25 }}>Utilizatori</Typography>
            <Box display="flex" gap={1.5} flexWrap="wrap">
              <Box display="flex" alignItems="center"><PeopleIcon color="primary" sx={{ fontSize: 14, mr: 0.25 }} /><Typography sx={{ fontSize: '0.72rem' }}>Total: {kpiData.users.total}</Typography></Box>
              <Box display="flex" alignItems="center"><AssessmentIcon color="success" sx={{ fontSize: 14, mr: 0.25 }} /><Typography sx={{ fontSize: '0.72rem' }}>Medici: {kpiData.users.medics}</Typography></Box>
              <Box display="flex" alignItems="center"><ScheduleIcon color="info" sx={{ fontSize: 14, mr: 0.25 }} /><Typography sx={{ fontSize: '0.72rem' }}>Op: {kpiData.users.operators}</Typography></Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Detailed Statistics - Compact & Fixed Height */}
      <Grid container spacing={1.5} alignItems="stretch">
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <Paper sx={{ p: 1.5, bgcolor: 'background.paper', width: '100%', height: 100 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 0.5 }}>Statistici Studii</Typography>
            <Box display="flex" justifyContent="space-between" mb={0.25}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Urgente</Typography><Chip label={kpiData.studies.urgent} size="small" color="error" sx={{ height: 18, fontSize: '0.68rem' }} /></Box>
            <Box display="flex" justifyContent="space-between" mb={0.25}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Normale</Typography><Chip label={kpiData.studies.normal} size="small" color="primary" sx={{ height: 18, fontSize: '0.68rem' }} /></Box>
            <Box display="flex" justifyContent="space-between" mb={0.25}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Reasociate</Typography><Chip label={kpiData.studies.reassigned} size="small" color="warning" sx={{ height: 18, fontSize: '0.68rem' }} /></Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <Paper sx={{ p: 1.5, bgcolor: 'background.paper', width: '100%', height: 100 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 0.5 }}>Gărzi & Programări</Typography>
            <Box display="flex" justifyContent="space-between" mb={0.25}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Total</Typography><Chip label={kpiData.shifts.total} size="small" color="primary" sx={{ height: 18, fontSize: '0.68rem' }} /></Box>
            <Box display="flex" justifyContent="space-between" mb={0.25}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Active</Typography><Chip label={kpiData.shifts.active} size="small" color="success" sx={{ height: 18, fontSize: '0.68rem' }} /></Box>
            <Box display="flex" justifyContent="space-between" mb={0.25}><Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Completate</Typography><Chip label={kpiData.shifts.completed} size="small" color="info" sx={{ height: 18, fontSize: '0.68rem' }} /></Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Footer */}
      <Box mt={2} display="flex" justifyContent="center">
        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Ultima actualizare: {new Date().toLocaleString('ro-RO')}</Typography>
      </Box>
    </Box>
  )
}

export default KPIPage
